import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CoinDataProvider } from './contexts/CoinDataContext';
import { AppSettingsProvider } from './contexts/AppSettingsContext';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './layouts/MainLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ExchangeSettings from './pages/ExchangeSettings';
import Trading from './pages/Trading';
import History from './pages/History';
import Settings from './pages/Settings';
import ProfileSettings from './pages/ProfileSettings';
import ArbitrageOpportunities from './pages/ArbitrageOpportunities';
import SpamSell from './pages/SpamSell';
import SpamSellWorker from './pages/SpamSellWorker';
import { buildApiUrl } from './config';

function AppContent() {
  const { logout } = useAuth();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch(buildApiUrl('/account/manage/info'), {
          credentials: 'include'
        });

        if (response.status === 401) {
          await logout();
        }
      } catch (error) {
        console.error('Failed to validate session on app start', error);
      }
    };

    checkSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected Routes */}
        {/* Dashboard route commented out since Home is disabled
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            */}

        <Route
          path="/arbitrage"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ArbitrageOpportunities />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/spam-sell"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SpamSell />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/spam-sell-worker"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SpamSellWorker />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/exchanges"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ExchangeSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/trade"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Trading />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <MainLayout>
                <History />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfileSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Default Route */}
        <Route path="/" element={<Navigate to="/arbitrage" replace />} />
      </Routes>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          className: '',
          style: {
            background: '#1F2937',
            color: '#fff',
            border: '1px solid #374151',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppSettingsProvider>
          <CoinDataProvider>
            <AppContent />
          </CoinDataProvider>
        </AppSettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
